import { FC, ReactNode, useRef, useState } from 'react';

import toast from 'react-hot-toast';

import { IconEye } from '@tabler/icons-react';

import { QueryResult, useApolloClient, useMutation, useQuery } from '@apollo/client';

import { ProtectedDownloadPurpose, SaleBidderLevel } from '__generated__/globalTypes';
import { Button, useDialog } from 'ui-kit';
import ConfirmDialog from 'ui-kit/common/ConfirmDialog';

import { ApproveSale, ApproveSaleVariables } from 'mutation/__generated__/approveSale';
import {
  CreateProtectedDownload,
  CreateProtectedDownloadVariables,
} from 'mutation/__generated__/createProtectedDownload';
import { APPROVE_SALE } from 'mutation/approveSale';
import { CREATE_PROTECTED_DOWNLOAD } from 'mutation/createProtectedDownload';

import { GetUserInfo } from 'query/__generated__/userData';
import {
  MySaleReviewerAccess,
  MySaleReviewerAccessVariables,
  SaleApproval,
  SaleApprovalVariables,
  SaleDocuments,
  SaleDocumentsVariables,
} from 'query/__generated__/saleDataRoom';
import {
  MY_SALE_REVIEWER_ACCESS_QUERY,
  SALE_APPROVAL_QUERY,
  SALE_DOCUMENTS_QUERY,
} from 'query/saleDataRoom';
import { COMPANY_SALES_QUERY, OPEN_SALES_QUERY } from 'query/sales';
import { CompanySales } from 'query/__generated__/sales';
import { GET_USER_INFO } from 'query/userData';

import { SaleDataRoomTree, VIEW_ACTION_CLASS } from './SaleDataRoomTree';
import {
  approveSaleRefusalCopy,
  countDataRoomDocuments,
  isAlreadyApproved,
  SaleDataRoomDocument,
} from './saleDataRoom';
import { formatDateTime } from './salesUtils';
import { PRIMARY_BUTTON_LIGHT_THEME, SECONDARY_BUTTON_LIGHT_THEME } from './styles';

export const REVIEWER_GRANT_NOTE =
  'Opening documents before approval needs a reviewer grant from an Orsnn operator.';

export const APPROVED_COPY = 'Sale approved. Bidders can now request access.';

export const COUNT_FAILED_COPY = "Couldn't count the documents, so the sale can't be approved yet.";

export const POPUP_BLOCKED_COPY = 'Allow pop-ups for this site to open the review copy.';

const pluralizeDocuments = (count: number) => `${count} document${count === 1 ? '' : 's'}`;

export const dataRoomChangedCopy = (count: number) =>
  `The data room changed while you were reviewing it. It now has ${pluralizeDocuments(count)}; review it, then approve again.`;

export const recordedCountMismatchCopy = (recorded: number, shown: number) =>
  `The approval recorded ${pluralizeDocuments(recorded)}, not the ${shown} you reviewed. Check the data room.`;

export const approvalConfirmMessage = (count: number) =>
  `You're approving ${pluralizeDocuments(count)}. Bidders can request access once you approve.`;

export type ViewToggleOption = {
  level: SaleBidderLevel;
  label: string;
  // An option the viewer can't choose: it stays focusable and shows this on hover and focus.
  unavailableReason?: ReactNode;
};

const SELLER_VIEW_OPTIONS: ViewToggleOption[] = [
  { level: SaleBidderLevel.L2, label: 'Full (L2)' },
  { level: SaleBidderLevel.L1, label: 'L1 bidder view' },
];

const VIEW_OPTION_CLASS = 'px-3 py-1 text-xs font-medium';

// WCAG 1.4.13: the reason stays open while the pointer is over it or focus is in it, either one,
// so its link can be reached. Escape dismisses it until the next hover or focus. Escape first
// moves focus to the option, since a browser drops focus to the page when a focused link is hidden.
const UnavailableViewOption: FC<{
  level: SaleBidderLevel;
  label: string;
  reason: ReactNode;
}> = ({ level, label, reason }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const optionRef = useRef<HTMLButtonElement>(null);
  const open = (hovered || focused) && !dismissed;
  const reasonId = `sale-data-room-view-${level}-unavailable`;

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => {
        setHovered(true);
        setDismissed(false);
      }}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => {
        setFocused(true);
        setDismissed(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          optionRef.current?.focus();
          setDismissed(true);
        }
      }}
    >
      <button
        ref={optionRef}
        type="button"
        data-testid={`sale-data-room-view-${level}`}
        aria-pressed={false}
        aria-disabled="true"
        aria-describedby={reasonId}
        className={`${VIEW_OPTION_CLASS} cursor-not-allowed text-gray-500 light:text-gray-600`}
      >
        {label}
      </button>
      <div id={reasonId} hidden={!open} className="absolute right-0 top-full z-10 w-[340px] pt-2">
        <div className="rounded-md border border-gray-700 bg-background-subtle px-3 py-2.5 text-left text-[12.5px] leading-[1.45] text-gray-200 shadow-lg light:border-gray-300 light:bg-white light:text-gray-800">
          {reason}
        </div>
      </div>
    </span>
  );
};

export const ViewToggle: FC<{
  viewAs: SaleBidderLevel;
  onChange: (level: SaleBidderLevel) => void;
  label?: string;
  options?: ViewToggleOption[];
}> = ({ viewAs, onChange, label = 'Data room view', options = SELLER_VIEW_OPTIONS }) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex rounded border border-slate-500 light:border-gray-600"
  >
    {options.map(({ level, label, unavailableReason }) =>
      unavailableReason ? (
        <UnavailableViewOption key={level} level={level} label={label} reason={unavailableReason} />
      ) : (
        <button
          key={level}
          type="button"
          data-testid={`sale-data-room-view-${level}`}
          aria-pressed={viewAs === level}
          onClick={() => onChange(level)}
          className={`${VIEW_OPTION_CLASS} ${
            viewAs === level
              ? 'bg-pink-700 text-white'
              : 'text-gray-300 hover:bg-slate-700 light:text-gray-700 light:hover:bg-gray-200'
          }`}
        >
          {label}
        </button>
      ),
    )}
  </div>
);

const OpenReviewCopyButton: FC<{ document: SaleDataRoomDocument; active: boolean }> = ({
  document,
  active,
}) => {
  const [opening, setOpening] = useState(false);
  const [createProtectedDownload] = useMutation<
    CreateProtectedDownload,
    CreateProtectedDownloadVariables
  >(CREATE_PROTECTED_DOWNLOAD);

  const handleOpen = async () => {
    const viewer = window.open('', '_blank');
    if (!viewer) {
      toast.error(POPUP_BLOCKED_COPY);
      return;
    }
    setOpening(true);
    try {
      const { data } = await createProtectedDownload({
        variables: {
          documentId: document.document_id,
          purpose: ProtectedDownloadPurpose.VIEW,
          asReviewer: true,
        },
      });
      const url = data?.createProtectedDownload.url;
      if (!url) {
        throw new Error("Couldn't open the review copy.");
      }
      viewer.opener = null;
      viewer.location.href = url;
    } catch (openError) {
      viewer.close();
      toast.error(
        openError instanceof Error ? openError.message : "Couldn't open the review copy.",
      );
    } finally {
      setOpening(false);
    }
  };

  const disabled = !active || opening;
  return (
    <button
      type="button"
      data-testid="sale-open-review-copy"
      disabled={disabled}
      onClick={handleOpen}
      className={VIEW_ACTION_CLASS}
    >
      <IconEye aria-hidden="true" size={16} stroke={1.75} />
      Open review copy
    </button>
  );
};

export const ReviewCopyDataRoom: FC<{
  saleId: string;
  viewAs: SaleBidderLevel;
  onViewAsChange: (level: SaleBidderLevel) => void;
  dataRoomQuery: Pick<
    QueryResult<SaleDocuments, SaleDocumentsVariables>,
    'data' | 'loading' | 'error'
  >;
  reviewerGrantNote: string;
}> = ({ saleId, viewAs, onViewAsChange, dataRoomQuery, reviewerGrantNote }) => {
  const reviewerQuery = useQuery<MySaleReviewerAccess, MySaleReviewerAccessVariables>(
    MY_SALE_REVIEWER_ACCESS_QUERY,
    { variables: { saleId }, errorPolicy: 'all' },
  );
  const dataRoom = dataRoomQuery.data?.saleDocuments;
  const reviewerActive = reviewerQuery.data?.mySaleReviewerAccess.active ?? false;

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <ViewToggle viewAs={viewAs} onChange={onViewAsChange} />
        {!reviewerQuery.loading && !reviewerActive && (
          <p
            data-testid="sale-reviewer-grant-note"
            className="text-xs text-gray-400 light:text-gray-700"
          >
            {reviewerGrantNote}
          </p>
        )}
      </div>

      <div className="mt-3">
        {dataRoomQuery.loading && !dataRoom ? (
          <p className="text-sm text-gray-400 light:text-gray-700">Loading data room…</p>
        ) : dataRoomQuery.error && !dataRoom ? (
          <p className="text-sm text-status-error light:text-error-muted">
            Failed to load the data room.
          </p>
        ) : dataRoom ? (
          <SaleDataRoomTree
            dataRoom={dataRoom}
            renderDocumentAction={(document) => (
              <OpenReviewCopyButton document={document} active={reviewerActive} />
            )}
          />
        ) : null}
      </div>
    </>
  );
};

export const SaleApprovalPanel: FC<{ saleId: string }> = ({ saleId }) => {
  const client = useApolloClient();
  const confirmDialog = useDialog();
  const [viewAs, setViewAs] = useState<SaleBidderLevel>(SaleBidderLevel.L2);
  const [submitting, setSubmitting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const viewQuery = useQuery<SaleDocuments, SaleDocumentsVariables>(SALE_DOCUMENTS_QUERY, {
    variables: { saleId, viewAs },
    errorPolicy: 'all',
  });
  const fullQuery = useQuery<SaleDocuments, SaleDocumentsVariables>(SALE_DOCUMENTS_QUERY, {
    variables: { saleId, viewAs: SaleBidderLevel.L2 },
    errorPolicy: 'all',
  });
  const [approveSale] = useMutation<ApproveSale, ApproveSaleVariables>(APPROVE_SALE, {
    update: (cache, { data }) => {
      const approved = data?.approveSale;
      if (!approved) {
        return;
      }
      cache.updateQuery<CompanySales>({ query: COMPANY_SALES_QUERY, variables: {} }, (current) =>
        current
          ? {
              ...current,
              companySales: current.companySales.map((sale) =>
                sale.__typename === 'Sale' && sale.sale_id === approved.sale_id
                  ? { ...sale, status: approved.status }
                  : sale,
              ),
            }
          : current,
      );
      cache.writeQuery<SaleApproval, SaleApprovalVariables>({
        query: SALE_APPROVAL_QUERY,
        variables: { saleId },
        data: {
          __typename: 'Query',
          sale: {
            __typename: 'Sale',
            sale_id: approved.sale_id,
            approved_by_user_id: approved.approved_by_user_id,
            approved_at: approved.approved_at,
            approved_document_count: approved.approved_document_count,
          },
        },
      });
    },
    refetchQueries: [
      { query: COMPANY_SALES_QUERY, variables: {} },
      { query: OPEN_SALES_QUERY, variables: {} },
      { query: SALE_APPROVAL_QUERY, variables: { saleId } },
    ],
  });

  const fullDataRoom = fullQuery.data?.saleDocuments;
  const documentCount = fullDataRoom ? countDataRoomDocuments(fullDataRoom) : undefined;
  const approveDisabled = submitting || documentCount === undefined;

  const refetchSaleViews = async () => {
    try {
      await client.refetchQueries({
        include: [COMPANY_SALES_QUERY, OPEN_SALES_QUERY, SALE_APPROVAL_QUERY],
      });
    } catch {
      return;
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    setRefusal(null);
    try {
      const { data: latest } = await fullQuery.refetch();
      if (!latest?.saleDocuments) {
        throw new Error("Couldn't count the documents.");
      }
      const latestCount = countDataRoomDocuments(latest.saleDocuments);
      if (latestCount !== documentCount) {
        setRefusal(dataRoomChangedCopy(latestCount));
        setSubmitting(false);
        if (viewAs !== SaleBidderLevel.L2) {
          await viewQuery.refetch().catch(() => undefined);
        }
        return;
      }
      const { data } = await approveSale({ variables: { saleId } });
      toast.success(APPROVED_COPY);
      const recorded = data?.approveSale?.approved_document_count;
      if (recorded != null && recorded !== latestCount) {
        toast.error(recordedCountMismatchCopy(recorded, latestCount));
      }
    } catch (approveError) {
      setSubmitting(false);
      if (isAlreadyApproved(approveError)) {
        toast.error(approveSaleRefusalCopy(approveError));
        await refetchSaleViews();
        return;
      }
      setRefusal(approveSaleRefusalCopy(approveError));
    }
  };

  return (
    <section
      data-testid="sale-approval-panel"
      aria-label="Review and approve"
      className="mt-6 rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-gray-100"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heebo text-lg font-medium text-white light:text-gray-900">
            Review and approve
          </h2>
          <p className="mt-1 text-sm text-gray-400 light:text-gray-700">
            Bidders can't see this sale until you approve it.
          </p>
        </div>
        <Button
          size="small"
          type="primary"
          label="Approve"
          data-testid="sale-approve"
          disabled={approveDisabled}
          className={approveDisabled ? '' : PRIMARY_BUTTON_LIGHT_THEME}
          onClick={confirmDialog.openDialog}
        >
          Approve
        </Button>
      </div>

      {fullQuery.error && !fullDataRoom && (
        <div className="mt-3 flex items-center gap-3">
          <p
            data-testid="sale-approval-count-failed"
            className="text-sm text-status-error light:text-error-muted"
          >
            {COUNT_FAILED_COPY}
          </p>
          <Button
            size="small"
            type="secondary"
            label="Retry"
            data-testid="sale-approval-count-retry"
            className={SECONDARY_BUTTON_LIGHT_THEME}
            onClick={() => {
              void fullQuery.refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {refusal && (
        <p
          role="alert"
          data-testid="sale-approval-refusal"
          className="mt-3 text-sm text-status-error light:text-error-muted"
        >
          {refusal}
        </p>
      )}

      <ReviewCopyDataRoom
        saleId={saleId}
        viewAs={viewAs}
        onViewAsChange={setViewAs}
        dataRoomQuery={viewQuery}
        reviewerGrantNote={REVIEWER_GRANT_NOTE}
      />

      <ConfirmDialog
        dialog={confirmDialog}
        message={approvalConfirmMessage(documentCount ?? 0)}
        confirmButtonText="Approve sale"
        onConfirm={handleApprove}
      />
    </section>
  );
};

export const SaleApprovalLine: FC<{ saleId: string }> = ({ saleId }) => {
  const { data: approvalData } = useQuery<SaleApproval, SaleApprovalVariables>(
    SALE_APPROVAL_QUERY,
    { variables: { saleId }, errorPolicy: 'all' },
  );
  const { data: userData } = useQuery<GetUserInfo>(GET_USER_INFO, { errorPolicy: 'all' });

  const approval = approvalData?.sale?.__typename === 'Sale' ? approvalData.sale : undefined;
  if (!approval?.approved_at) {
    return null;
  }

  const user = userData?.user;
  const approver = (() => {
    if (!user || !approval.approved_by_user_id) {
      return undefined;
    }
    if (approval.approved_by_user_id !== user.id) {
      return 'another user';
    }
    return [user.given_name, user.family_name].filter(Boolean).join(' ') || user.email;
  })();

  return (
    <p data-testid="sale-approval-line" className="mt-6 text-sm text-gray-300 light:text-gray-700">
      Approved{approver ? ` by ${approver}` : ''} on {formatDateTime(approval.approved_at)}
      {approval.approved_document_count !== null &&
        ` · ${pluralizeDocuments(approval.approved_document_count)}`}
    </p>
  );
};
